import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { validateResetPasswordForm, hasFormErrors } from '../../utils/validation';
import { Mail, ArrowLeft } from 'lucide-react';
import type { ResetPasswordFormData, FormErrors } from '../../types/auth';

interface ForgotPasswordFormProps {
  onSuccess?: () => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onSuccess }) => {
  const { resetPassword, loading, error, clearError } = useAuth();
  const [formData, setFormData] = useState<ResetPasswordFormData>({ email: '' });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    if (formErrors[name as keyof FormErrors]) setFormErrors(p => ({ ...p, [name]: undefined }));
    if (error) clearError();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateResetPasswordForm(formData);
    setFormErrors(errors);
    if (hasFormErrors(errors)) return;
    const { error: resetError } = await resetPassword(formData.email);
    if (!resetError) {
      setIsSuccess(true);
      if (onSuccess) onSuccess();
    }
  };

  if (isSuccess) {
    return (
      <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
        <div style={{
          width: 52, height: 52, borderRadius: '50%',
          background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 1.125rem',
        }}>
          <Mail size={22} color="#60a5fa" strokeWidth={2} />
        </div>
        <h2 style={{ fontSize: '1.375rem', fontWeight: 800, color: '#f1f5f9', marginBottom: '0.5rem', letterSpacing: '-0.03em' }}>
          Check Your Email
        </h2>
        <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1.5rem', lineHeight: 1.65 }}>
          We sent a reset link to{' '}
          <span style={{ color: '#94a3b8', fontWeight: 600 }}>{formData.email}</span>.
          Follow the instructions to reset your password.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', alignItems: 'center' }}>
          <Link
            to="/auth/login"
            style={{ color: '#3b82f6', fontWeight: 600, fontSize: '0.875rem' }}
          >
            Return to Sign In
          </Link>
          <button
            onClick={() => { setIsSuccess(false); setFormData({ email: '' }); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#334155', fontSize: '0.8125rem', fontWeight: 500, padding: 0 }}
          >
            Try a different email
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: '1.75rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.03em', marginBottom: '0.375rem' }}>
          Reset password
        </h2>
        <p style={{ fontSize: '0.875rem', color: '#475569', lineHeight: 1.6 }}>
          Enter your email and we'll send you a reset link.
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <Input
          label="Email Address"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          error={formErrors.email}
          placeholder="you@example.com"
          required
          autoComplete="email"
        />

        {error && (
          <div
            role="alert"
            style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '0.75rem 0.875rem', fontSize: '0.8125rem', color: '#f87171' }}
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={loading}
          style={{ width: '100%', marginTop: '0.25rem', borderRadius: 9 }}
        >
          {loading ? 'Sending…' : 'Send Reset Link'}
        </Button>
      </form>

      <div style={{ marginTop: '1.25rem', textAlign: 'center' }}>
        <Link
          to="/auth/login"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#334155', fontSize: '0.8125rem', fontWeight: 500, transition: 'color 0.18s' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#94a3b8'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#334155'; }}
        >
          <ArrowLeft size={13} strokeWidth={2.5} /> Back to sign in
        </Link>
      </div>
    </div>
  );
};
